<template>
  <div class="PlanComparisonTable" :class="{ 'PlanComparisonTable--mobile': isMobile }">
    <ComparisonCardsMobile
      v-if="isMobile"
      :rows
      :columns="decoratedColumns"
      :comparingLabel
      :pageLabel
      @submit="emit('submit', $event)"
      @back="emit('back')"
    />

    <ComparisonTableDesktop
      v-else
      :rows
      :columns="decoratedColumns"
      :pageLabel
      @submit="emit('submit', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useResponsive } from '@cvo/ui/composables';
import ComparisonCardsMobile from './ComparisonCardsMobile.vue';
import ComparisonTableDesktop from './ComparisonTableDesktop.vue';
import type {
  BasePlanComparisonTableProps,
  ColumnPresentation,
  ComparisonColumnState,
} from './BasePlanComparisonTable.model';

const props = defineProps<BasePlanComparisonTableProps>();

const emit = defineEmits<{
  (e: 'submit', planId: string): void;
  (e: 'back'): void;
}>();

const { isMobile } = useResponsive();

/**
 * How each column state presents itself. Every purchasable plan leads with the primary CTA; the
 * secondary treatment is reserved for coming-soon plans.
 */
const STATE_PRESENTATION: Record<ComparisonColumnState, ColumnPresentation> = {
  'default': { stateClass: undefined, ctaVariant: 'primary', tag: { type: 'neutral', variant: 'subtle' } },
  'promotion': { stateClass: 'is-promotion', ctaVariant: 'primary', tag: { type: 'highlight', variant: 'filled' } },
  'coming-soon': { stateClass: 'is-comingSoon', ctaVariant: 'secondary', tag: { type: 'neutral', variant: 'subtle' } },
};

// Resolved once per column rather than on every read, since each layout reads it per cell.
const decoratedColumns = computed(() => props.columns.map(column => ({
  ...column,
  presentation: STATE_PRESENTATION[column.state ?? 'default'],
})));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .PlanComparisonTable {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-4;
    align-items: center;
    width: 100%;

    &--mobile {
      align-items: stretch;
    }
  }
}
</style>
